// The architecture figure, as DATA — one model, rendered to mermaid by the
// function at the bottom and checked against the repository by
// src/test/architecture.test.js.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 WHY THIS IS A MODEL AND NOT A TEMPLATE STRING
// ═══════════════════════════════════════════════════════════════════════════
//
// The figure this replaces drew EIGHT nodes and THREE of them were the trivia
// Activity — a system Jordan retired in P6-116 (its commands) and P6-138 (both
// of its deployables, `activity/backend/` and `activity/frontend/`, deleted
// trees). The only edge that touched the website was a dotted line to the
// trivia service. Meanwhile `music/` and `trips/` — two live deployables — had
// never had a node, and neither had the bot's directory publish.
//
// So the picture centred the one thing that no longer exists and omitted half
// of what does. It is the most visible wrong thing this repo ships: the site is
// the only part of the estate the six people who use it can see.
//
// A corrected template string would rot again on the next merge, and this has
// now happened twice in one month — once by ADDITION (music, trips) and once by
// DELETION (the Activity). CLAUDE.md's own deployable count has been wrong in
// BOTH directions for exactly the same reason: every new deployable is somebody
// else's PR, and nobody's PR is "update the picture".
//
// ⚠️ A test that read the diagram and asserted a hardcoded node list would be a
// SECOND COPY of the same claim, rotting in parallel. The gate therefore
// DERIVES what must be here:
//
//   · every Dockerfile in the repo must be a node, and every node claiming to
//     be a deployable must name a Dockerfile that exists — a bijection, so the
//     figure moves when the estate does, in either direction;
//   · every entry of bot.py's COGS must be either drawn by a node or listed in
//     NOT_DRAWN with a reason, and nothing may draw a cog that COGS no longer
//     has (which is precisely what the three trivia nodes were);
//   · every node names repository paths that must exist, so a node for a system
//     that is not here cannot be added at all.
//
// ═══════════════════════════════════════════════════════════════════════════
// WHAT WAS MEASURED, AND HOW — 2026-08-31
// ═══════════════════════════════════════════════════════════════════════════
//
// Nothing below was copied out of a document. CLAUDE.md, docs/d2d-architecture.md
// and docs/RAILWAY_CONFIG.md each state part of this and each is stale in a
// different place (the architecture doc says so about itself; RAILWAY_CONFIG
// still has music on Railway).
//
//   FOUR DEPLOYABLES.  `find . -name Dockerfile -not -path "*/node_modules/*"
//                      -not -path "./.claude/*"` returns ./Dockerfile,
//                      ./music/Dockerfile, ./site/Dockerfile, ./trips/Dockerfile.
//   WHAT THE BOT RUNS. bot.py's COGS: hoops, movie, admin, bug, bug_loop, idea,
//                      squadup, directory, projections, help. `cogs.activity` is
//                      gone (deleted, not shelved); `cogs.hoopsickos` (P6-44) and
//                      `cogs.fantasy` (P6-166) are both in SHELVED_COGS and
//                      nothing loads either. ⚠️ Re-derive this from bot.py rather
//                      than reading it here — the gate below does exactly that,
//                      and this line is a note, not a source.
//   WHERE THEY RUN.    bot -> Railway (railway.bot.toml is the live config file;
//                      scripts/prod_drift_probe.py carries its service id).
//                      trips -> Railway with its own Postgres (trips/railway.toml
//                      healthcheckPath /health; trips/migrations).
//                      site -> Cloudflare Pages + Functions (the Site Deploy job).
//                      music -> NOT Railway. docs/ledger.md records the completed
//                      cutover to vegapunk's k3s, and ci.yml's GHCR Publish builds
//                      `d2d-music` from music/Dockerfile expressly to feed it.
//                      ⚠️ The cluster's own manifests live in another repo, so
//                      that last hop is the one claim here read off a document
//                      rather than driven — which is a second reason the figure
//                      draws no hosting at all.
//
// 📌 The k3s detail is deliberately NOT drawn. The figure is about the SHAPE of
// the system — who owns what, and what a Thursday depends on — and which cloud
// a deployable happens to sit in is exactly the kind of fact that rots fastest
// (music has now moved once). Drawing hosting would put a claim on the public
// site that nothing in this repo can keep true.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 PROVENANCE STRINGS ARE IN THE BUNDLE, DELIBERATELY
// ═══════════════════════════════════════════════════════════════════════════
//
// `dockerfile`, `evidence` and `cogs` ship in the built JavaScript, because
// keeping them here is what binds the drawing to its justification — split into
// a second file, the two drift, which is the failure this whole module exists
// to remove. They are ordinary source-file names: no path on a live host, no
// credential name, no open defect. site/publishing-guard.json is the backstop
// that enforces that distinction, and `npm run build` runs it over dist.

/**
 * @typedef {object} Node
 * @property {string} id            mermaid node id. Uppercase by convention.
 * @property {string} label         what a member reads. Plain language; `<br/>`
 *                                  is a real line break in mermaid and is how
 *                                  the figure's width is controlled.
 * @property {'deployable'|'store'|'outside'} kind
 * @property {string} [dockerfile]  deployables only, relative to the repo root.
 * @property {string} [owner]       stores only: the node id that owns it.
 *                                  Principle 2 — one owner per store.
 * @property {string[]} evidence    repo-root-relative paths that must exist.
 * @property {string[]} [cogs]      entries of bot.py's COGS this node draws.
 */

/** @type {Node[]} */
export const NODES = [
  {
    id: 'DISCORD',
    label: 'Discord',
    kind: 'outside',
    // Not just the way in — Discord IS a datastore here (CLAUDE.md gotcha 9):
    // message ids are persisted and scheduled lifecycles are restored at boot.
    // Drawn as the outside world rather than a store because that is the honest
    // public reading, and because nothing in this repo owns it.
    evidence: ['bot.py'],
  },
  {
    id: 'MUSIC',
    label: 'The music bot',
    kind: 'deployable',
    dockerfile: 'music/Dockerfile',
    // Its own Discord application, its own image, its own store, and NOT a cog
    // in the bot — ADR-0004 D1. That is why it has no `cogs` entry: nothing in
    // bot.py's COGS belongs to it.
    evidence: ['music/main.py', 'music/utils/player.py'],
  },
  {
    id: 'TUNES',
    label: 'Its own<br/>store',
    kind: 'store',
    owner: 'MUSIC',
    evidence: ['music/utils/store.py'],
  },
  {
    id: 'BOT',
    label: 'The bot',
    kind: 'deployable',
    dockerfile: 'Dockerfile',
    evidence: ['bot.py', 'cogs/movie/cog.py'],
    // The features a member would name. `cogs.directory` is drawn on the
    // NAMES node instead, and the rest are in NOT_DRAWN with reasons — the
    // gate makes that a decision rather than an omission. No count is written
    // here on purpose: CLAUDE.md's most rot-prone thing is a number in prose,
    // and the gate derives the accounting from bot.py either way.
    // 🔴 `cogs.fantasy` left this list when P6-166 shelved it (Jordan,
    // 2026-09-04) and is back in bot.py's COGS since P6-326 -- but SWITCHED OFF
    // until the bot owner connects it to Yahoo, so it is in NOT_DRAWN below with
    // that reason rather than drawn here. It earns a node when it is live.
    cogs: ['cogs.movie', 'cogs.hoops', 'cogs.squadup'],
  },
  {
    id: 'FILES',
    label: 'Its files',
    kind: 'store',
    owner: 'BOT',
    evidence: ['utils/movie_manager.py', 'utils/atomic_json.py'],
  },
  {
    id: 'DATA',
    // Fantasy left this label with P6-166: the bot did not call Yahoo while
    // `cogs.fantasy` was shelved, and a public figure naming it would be the
    // same defect P6-165 took the fantasy page down for, one artifact over.
    // It is loaded again since P6-326 but switched off until the owner connects
    // it, so the label still does not claim it. The evidence path stays because
    // the module does.
    label: 'Film and NBA',
    kind: 'outside',
    evidence: ['utils/nba_live.py', 'utils/yahoo_api.py'],
  },
  {
    id: 'TRIPS',
    label: 'The trip<br/>service',
    kind: 'deployable',
    dockerfile: 'trips/Dockerfile',
    evidence: ['trips/main.py'],
  },
  {
    id: 'TRIPDB',
    label: 'Its own<br/>database',
    kind: 'store',
    owner: 'TRIPS',
    evidence: ['trips/utils/db.py', 'trips/migrations'],
  },
  {
    id: 'SITE',
    label: 'This website',
    kind: 'deployable',
    dockerfile: 'site/Dockerfile',
    evidence: ['site/src/App.jsx', 'site/functions/_middleware.js'],
  },
  {
    id: 'NAMES',
    label: "Everyone's<br/>names",
    kind: 'store',
    // 🔴 The bot owns it and the website only reads it. That asymmetry is the
    // whole point of the node: ADR-0003 D2 stores no user record on the site,
    // so the only way a page can name anybody is a file the bot publishes.
    owner: 'BOT',
    evidence: [
      'cogs/directory.py',
      'utils/member_directory.py',
      'site/functions/_lib/projections.js',
    ],
    cogs: ['cogs.directory'],
  },
]

/**
 * Cogs bot.py loads that the figure deliberately does not draw.
 *
 * 🔴 THIS IS THE FAIL-CLOSED HALF AND IT IS THE POINT OF THE WHOLE FILE. The
 * gate requires every entry of COGS to appear either on a node or here, so a
 * cog that lands next month reds the site suite until somebody decides which it
 * is. An editorial choice is fine; a silent omission is what produced a figure
 * with three trivia nodes in it.
 *
 * The reason strings are asserted to be substantial for the same reason
 * site/publishing-guard.json asserts its markers carry one: a token reason is
 * how a forcing function becomes a formality.
 */
export const NOT_DRAWN = {
  'cogs.admin':
    'Server configuration. It has no store, no outside dependency and no shape of its own — it is a settings surface on the bot node that is already drawn.',
  'cogs.bug':
    '/bug files a GitHub issue. Real, but it is a member telling us something is broken rather than a part of how the system runs, and a node for it would imply the bot depends on GitHub to work.',
  'cogs.bug_loop':
    'The follow-up half of /bug, split out precisely so it cannot stop a bug being reported. Drawing it would give the split a prominence the split exists to avoid.',
  'cogs.idea':
    'Banks an idea as a GitHub issue, same shape and same reasoning as cogs.bug. Off the figure for the same reason.',
  'cogs.help':
    'Renders whatever is in the command tree at the moment it is asked. It reads the bot and nothing else, so it has nowhere to be on a diagram about who talks to what.',
  // 🔴 P6-155. The RETIREMENT CONDITION is written next to the compromise on
  // purpose — CLAUDE.md gotcha 6 records that as the one pattern here that did
  // not rot, because the reason was falsifiable and sat where the next reader
  // would be standing. It is: the day a signed-in member opens a portal page
  // built from this, generalise the NAMES node (it is already "a file the bot
  // publishes that the site only reads") rather than adding a second store
  // beside it, and delete this entry.
  'cogs.projections':
    'Publishes movie history and each member\'s own record to R2 for the site to read. Deliberately off the figure because it adds no new SHAPE: the bot-owns-it, site-only-reads-it arrow is already drawn by the names store, and a second node beside it would say there are two mechanisms when there is one. It is also a claim the public figure cannot yet keep — the portal that reads these files does not exist.',
  // 🔴 P6-4-C1. Ambient capture and the poster-only view (Name it / Not a
  // card) are real and live in Discord the moment this cog loads, but the
  // bytes and the record have nowhere to be READ from yet: the projection
  // producer, the image Function and the portal panel are all C4, a
  // separate, not-yet-landed slice. Same reasoning as cogs.projections just
  // above — drawing a node now would be a shape the public figure cannot
  // keep, since there is nothing on the site side of it to point to. Revisit
  // when C4 gives the site something to read (the binder/card-page panel),
  // at which point this earns a node the way NAMES did for the directory.
  'cogs.cards':
    'Ambient capture and its poster-only buttons are live in Discord, but the images (R2) and the record (the bot\'s own store) have no portal reader yet — the projection, the image Function and the panel are all C4. A node here would draw a shape the site side cannot keep until that slice lands.',
  // 🔴 W0. `/watch add|list|progress|watching|finish|drop|remove|info` are
  // real and live in Discord the moment this cog loads, but the projection
  // (3a/watch.json) and the portal's watch tab have nowhere to be READ from
  // yet -- the producer and the tab are W2/W3, separate, not-yet-landed
  // slices of the same spec. Same reasoning as cogs.cards and
  // cogs.projections just above -- drawing a node now would be a shape the
  // public figure cannot yet keep. Revisit when W2 gives the site something
  // to read, at which point this earns a node the way NAMES did for the
  // directory.
  // 🔴 P6-326. Loaded again (it was shelved by P6-166), but SWITCHED OFF: with no
  // Yahoo session every command answers "not connected", and the session only
  // exists once the bot owner runs `/fantasy connect`. A node would draw the
  // bot talking to Yahoo, a shape the public figure cannot keep until that has
  // happened in production. Retirement condition: the first connected boot --
  // draw it, add Yahoo to the DATA node and delete this entry.
  'cogs.fantasy':
    'Loaded but switched off: until the bot owner connects it with /fantasy connect there is no Yahoo session, every fantasy command answers "not connected", and the bot does not call Yahoo. A node would draw a shape the public figure cannot keep until that happens in production.',
  'cogs.watch':
    'The /watch commands are live in Discord, but the projection (3a/watch.json) and its portal reader have no node yet — the producer and the tab are W2/W3. A node here would draw a shape the site side cannot keep until that slice lands.',
}

/**
 * @typedef {object} Edge
 * @property {string} from
 * @property {string} to
 * @property {boolean} [critical]  drawn in ember; the legend calls this the
 *                                 critical path.
 * @property {boolean} [dotted]
 * @property {string} why          not rendered — the reason the edge is true,
 *                                 so a reviewer can check it against the repo.
 */

/**
 * 🔴 ORDER IS STILL LOAD-BEARING — BUT FOR A COMPLETELY DIFFERENT REASON THAN
 * IT USED TO BE, AND THE OLD WARNING WOULD NOW SEND YOU THE WRONG WAY.
 *
 * ✅ The hazard it used to describe is GONE. mermaid's `linkStyle` addresses
 * edges BY INDEX, and this figure hardcoded `linkStyle 0,1` under a comment
 * begging the next person to append rather than insert — reorder, and the
 * legend silently starts describing the wrong two arrows, which is worse than
 * no legend because a reader believes it. The indices are COMPUTED from the
 * `critical` flags now, and src/test/architecture.test.js checks the emitted
 * lines against positions it works out for itself. Insert freely: the critical
 * path here is edges 2 and 3, which is a thing the old form could not express.
 *
 * 🔴 WHAT REPLACED IT: DECLARATION ORDER DRIVES DAGRE'S LAYOUT, AND A BAD ONE
 * MAKES THE FIGURE LIE ABOUT THE CRITICAL PATH WITHOUT BEING WRONG.
 * Measured in Chromium while building this, with the bot's edges declared
 * first: dagre put `The music bot` leftmost, so the ember `BOT -> FILES` arrow
 * routed the full width of the figure and passed straight through the music
 * node — reading, to anyone glancing at the legend, as though music were on the
 * movie-night path. Every assertion in the gate passed. Declaring each
 * deployable's subtree together (music, then the bot, then the site) puts each
 * one's children underneath it, and shrank the rendered figure from 800px to
 * 691px as a side effect.
 *
 * 📌 So the rule is no longer "append, never insert". It is: after changing
 * this list, LOOK AT IT. The gate can prove the graph says what the model says;
 * it cannot tell you the picture reads wrong.
 *
 * @type {Edge[]}
 */
export const EDGES = [
  {
    from: 'DISCORD',
    to: 'MUSIC',
    why: 'Its own Discord application and its own voice connection (music/utils/player.py). A separate deployable, not a cog — ADR-0004 D1.',
  },
  {
    from: 'MUSIC',
    to: 'TUNES',
    why: 'music/utils/store.py is the single owner of the music store, and it must never write the store the bot owns.',
  },
  {
    from: 'DISCORD',
    to: 'BOT',
    critical: true,
    why: 'The gateway connection in bot.py. Movie night is a conversation in Discord before it is anything else.',
  },
  {
    from: 'BOT',
    to: 'FILES',
    critical: true,
    why: 'utils/movie_manager.py writes the history through utils/atomic_json.py. These two edges are the whole Thursday: nothing else on this figure can stop one.',
  },
  {
    from: 'BOT',
    to: 'DATA',
    why: 'TMDb for films (cogs/movie) and nba_api via utils/nba_live.py. Both degrade rather than break. Yahoo (utils/yahoo_api.py) is the third and is not reached today — cogs.fantasy is loaded but switched off until the bot owner connects it (P6-326), so the module is present and nothing calls it.',
  },
  {
    from: 'BOT',
    to: 'TRIPS',
    why: 'cogs/squadup/trips.py through utils/trip_client.py — the bot captures trip entries from Discord.',
  },
  {
    from: 'BOT',
    to: 'NAMES',
    why: 'cogs/directory.py publishes utils/member_directory.py output so the website can name people. Best-effort by ADR-0003 D4.',
  },
  {
    from: 'DISCORD',
    to: 'SITE',
    dotted: true,
    why: 'Sign-in only: site/functions/auth/login.js sends a member to Discord and the callback establishes the session. Dotted because it happens once, not continuously.',
  },
  {
    from: 'SITE',
    to: 'TRIPS',
    why: 'site/functions/_lib/trips.js calls the trip service server-to-server; the browser never reaches it.',
  },
  {
    from: 'SITE',
    to: 'NAMES',
    why: 'site/functions/_lib/projections.js reads the published directory. Read-only — the site owns no member record (ADR-0003 D2).',
  },
  {
    from: 'TRIPS',
    to: 'TRIPDB',
    why: 'trips/utils/db.py against its own Postgres, with trips/migrations applied at startup.',
  },
]

/** mermaid's node syntax for each kind. A store is a cylinder because it is a
 * store; everything else is a box. */
function shapeFor(node) {
  return node.kind === 'store' ? `[("${node.label}")]` : `["${node.label}"]`
}

/** Indices of the edges the legend promises in ember. Derived, never written
 * down — see the comment above EDGES. */
export function criticalEdgeIndices(edges = EDGES) {
  return edges.map((edge, index) => (edge.critical ? index : -1)).filter((index) => index >= 0)
}

/** Indices of everything else, so the two `linkStyle` lines between them cover
 * every edge exactly once. A missing edge would render in mermaid's default
 * colour, which belongs to neither key in the legend. */
export function quietEdgeIndices(edges = EDGES) {
  return edges.map((edge, index) => (edge.critical ? -1 : index)).filter((index) => index >= 0)
}

/**
 * Render the model to mermaid source.
 *
 * The colours are passed in rather than read here: <Diagram> reads them off the
 * live document so the figure cannot drift from the palette the rest of the page
 * uses, and the test renders with sentinels so it can tell the two `linkStyle`
 * lines apart without knowing anything about the tokens.
 */
export function graph(accent, quiet, { nodes = NODES, edges = EDGES } = {}) {
  const declarations = nodes.map((node) => `  ${node.id}${shapeFor(node)}`)
  const links = edges.map((edge) => `  ${edge.from} ${edge.dotted ? '-.->' : '-->'} ${edge.to}`)

  const critical = criticalEdgeIndices(edges)
  const rest = quietEdgeIndices(edges)
  const styles = []
  if (critical.length) {
    styles.push(`  linkStyle ${critical.join(',')} stroke:${accent},stroke-width:2px`)
  }
  if (rest.length) {
    styles.push(`  linkStyle ${rest.join(',')} stroke:${quiet},stroke-width:1px`)
  }

  return ['flowchart TB', ...declarations, '', ...links, '', ...styles, ''].join('\n')
}
